import { Suspense } from 'react';
import { Routes } from '@datadog/browser-rum-react/react-router-v7';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';
import { BrowserRouter, Route } from 'react-router';

import { Analytics } from '@components/features/Analytics';
import Layout from '@components/features/Layout';
import { Monitoring } from '@components/features/Monitoring';
import TeamApp from '@routes/TeamApp/TeamApp';
import { lazyRoute } from '@utils/lazyRoute';

import {
  RequireAuthOutlet,
  RequireFreeAsvScansOutlet,
  RequireNotAuthedOutlet,
} from './Gateways';
import { LaunchDarklyProvider } from './LaunchDarklyProvider';
import * as Layouts from './Layouts';
import { MaintenanceRedirect } from './MaintenanceRedirect';
import {
  PrefixAppPath,
  PrefixTeamPath,
  RedirectToFirstTeam,
} from './RedirectToFirstTeam';
import { TeamRoot } from './TeamRoot';

const Admin = lazyRoute(() => import('@routes/Admin'));
const Vercel = lazyRoute(() => import('@routes/VercelIntegration'));
const TeamSettings = lazyRoute(() => import('./TeamSettings'));

const AuthGitHub = lazyRoute(() => import('@routes/AuthGithub'));
const OAuthCallback = lazyRoute(() => import('@routes/Auth/OAuthCallback'));
const BillingSuccess = lazyRoute(() => import('@routes/Auth/BillingSuccess'));

const NotificationCallback = lazyRoute(
  () => import('@routes/Auth/NotificationCallback')
);

const Invites = lazyRoute(() => import('@routes/Invites'));
const Login = lazyRoute(() => import('@routes/Auth/Login'));
const Register = lazyRoute(() => import('@routes/Auth/Register'));
const AsvScanTarget = lazyRoute(
  () => import('@routes/Auth/AsvOnboarding/ScanTarget')
);
const VerifyEmail = lazyRoute(() => import('@routes/Auth/VerifyEmail'));
const Callback = lazyRoute(() => import('@routes/Auth/Callback'));

function RootRouter() {
  return (
    <Suspense fallback={<Layout.Loader />}>
      <NuqsAdapter>
        <BrowserRouter>
          <LaunchDarklyProvider>
            <Analytics />
            <Monitoring />

            <MaintenanceRedirect />

            <Routes>
              <Route path="admin/*" element={<Admin />} />

              <Route element={<RequireNotAuthedOutlet />}>
                <Route path="login" element={<Login />} />
                <Route path="register" element={<Register />} />
              </Route>

              <Route path="asv" element={<RequireFreeAsvScansOutlet />}>
                <Route index element={<AsvScanTarget />} />
              </Route>
              <Route path="callback" element={<Callback />} />
              <Route path="verify" element={<VerifyEmail />} />
              <Route path="invites" element={<Invites />} />
              <Route path="oauth">
                <Route path="github" element={<AuthGitHub />} />
                <Route path="callback" element={<OAuthCallback />} />
              </Route>

              <Route path="webhooks">
                <Route
                  path="slack"
                  element={<NotificationCallback type="SLACK" />}
                />
              </Route>

              <Route path="notifications">
                <Route
                  path="slack"
                  element={<NotificationCallback type="SLACK" />}
                />
              </Route>

              <Route element={<RequireAuthOutlet />}>
                <Route element={<Layouts.AppLayout />}>
                  <Route index element={<RedirectToFirstTeam />} />
                  <Route element={<Layouts.TeamLayout />}>
                    <Route path=":teamUuid" element={<TeamRoot />} />
                    <Route
                      path=":teamUuid/settings/*"
                      element={<TeamSettings />}
                    />
                    <Route path=":teamUuid/:appUuid/*" element={<TeamApp />} />
                  </Route>
                  {/* Redirect generic `/settings` routes to current team */}
                  <Route path="settings/*" element={<PrefixTeamPath />} />
                  {/* Redirect generic `/app` routes to the current app */}
                  <Route path="app/*" element={<PrefixAppPath />} />
                </Route>

                <Route
                  path="integrations/vercel/callback"
                  element={<Vercel />}
                />
                <Route path="billing/success" element={<BillingSuccess />} />
              </Route>
            </Routes>
          </LaunchDarklyProvider>
        </BrowserRouter>
      </NuqsAdapter>
    </Suspense>
  );
}

export default RootRouter;
